import { z } from 'zod';

import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
} from '../constants/ValidationRules';

export const insertCourseSchema = z.object({
  templateGroupId: z.number().min(1, { message: 'Please select a template' }),
  title: z
    .string()
    .trim()
    .min(1, { message: 'Course name is required' })
    .max(VALIDATION_LIMITS.COURSE.TITLE_MAX, {
      message: VALIDATION_MESSAGES.maxChars(VALIDATION_LIMITS.COURSE.TITLE_MAX),
    }),
});

export const editCourseSchema = z.object({
  id: z.number(),
  title: z
    .string()
    .trim()
    .max(VALIDATION_LIMITS.COURSE.TITLE_MAX, {
      message: VALIDATION_MESSAGES.maxChars(VALIDATION_LIMITS.COURSE.TITLE_MAX),
    }),
});

export const courseSettingSchema = z.object({
  viewUrl: z.string().trim().nullable(),
  passcode: z.string().trim().nullable(),
  status: z.string().nullable(),
});

export const fontsListFormSchema = z.object({
  fonts: z.array(
    z.object({
      family: z.string().trim().min(1, { message: 'Font family is required' }),
      selected: z.boolean().default(false),
    })
  ),
});

export const downloadCourseSchema = z.object({
  orientation: z.enum(['portrait', 'landscape'], {
    required_error: 'Please select an orientation',
  }),
  language: z.string().min(1, { message: 'Please select a language' }),
});

export const createViewURLSchema = z.object({
  viewUrl: z
    .string()
    .trim()
    .min(1, { message: 'View URL is required' })
    .regex(/^[a-zA-Z0-9_-]+$/, {
      message:
        'View URL can only contain letters, numbers, hyphens, and underscores',
    }),
});

export const duplicateCourseSchema = z.object({
  clientId: z.number().min(1, { message: 'Please select a client' }),
});

export const statusCourseSchema = z.object({
  status: z.enum(['Completed', 'In Progress', 'Disabled'], {
    required_error: 'Please select a status',
  }),
});

export const restoreCourseSchema = z.object({
  courseVersionId: z
    .number()
    .min(1, { message: 'Please select a course version' }),
});

export const generateCourseTemplateByCourseSchema = z.object({
  courseId: z.number().min(1, { message: 'Please select a course' }),
  templateOptions: z
    .number()
    .min(0, { message: 'Please select a template option' }),
});

export const createCourseFromTemplateSchema = z.object({
  pageTemplateIds: z
    .array(z.number())
    .min(1, { message: 'Please select at least one template' }),
});

export type InsertCourseSchema = z.infer<typeof insertCourseSchema>;

export type EditCourseSchema = z.infer<typeof editCourseSchema>;

export type CourseSettingSchema = z.infer<typeof courseSettingSchema>;

export type FontsListFormSchema = z.infer<typeof fontsListFormSchema>;

export type DownloadCourseSchema = z.infer<typeof downloadCourseSchema>;

export type CreateViewURLSchema = z.infer<typeof createViewURLSchema>;

export type DuplicateCourseSchema = z.infer<typeof duplicateCourseSchema>;

export type StatusCourseSchema = z.infer<typeof statusCourseSchema>;

export type RestoreCourseSchema = z.infer<typeof restoreCourseSchema>;

export type GenerateCourseTemplateByCourseSchema = z.infer<
  typeof generateCourseTemplateByCourseSchema
>;

export type CreateCourseFromTemplateSchema = z.infer<
  typeof createCourseFromTemplateSchema
>;
